"use client"
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import { CheckCircle2 } from "lucide-react";
import SubscriptionButton from './SubscriptionButton';
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";

const PricingModal = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  const features = [
    "Read While Listening: Enhance your learning with synchronized audio and text",
    "Personalized Book Simplification: Transform the books you love",
    "Extensive Library: Choose from a vast collection of simplified books",
    "Community: Join or create a multilingual bookclub"
  ];

  const modalContent = (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-[#4a4e69] mb-4">Save $83 off with the yearly plan!</h2>
        <div className="inline-flex border-4 border-[#4a4e69] rounded-lg shadow-sm">
          <Toggle
            pressed={billingPeriod === 'monthly'}
            onPressedChange={() => setBillingPeriod('monthly')}
            className="px-4 py-2 rounded-l-md bg-white hover:bg-gray-100"
          >
            Monthly
          </Toggle>
          <Toggle
            pressed={billingPeriod === 'yearly'}
            onPressedChange={() => setBillingPeriod('yearly')}
            className="px-4 py-2 rounded-r-md bg-white hover:bg-gray-100"
          >
            Yearly
          </Toggle>
        </div>
      </div>

      <div className="grid md:grid-cols-1 lg:grid-cols-1 gap-8">
        <Card className="w-full max-w-md mx-auto bg-[#EFEFEF]">
          <CardHeader>
            <CardTitle className="text-2xl">Lumi Pro</CardTitle>
            <CardDescription>
              Lumi Pro enables you to read any book in any language at any reading level.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center mb-6">
              <span className="text-4xl font-bold">
                {billingPeriod === 'monthly' ? '$19.99' : '$157.99'}
              </span>
              <span className="text-xl text-gray-600"> {billingPeriod}</span>
            </div>
            <SubscriptionButton 
              isPro={false} 
              billingPeriod={billingPeriod} 
              className="w-full mb-6 bg-[#4a4e69] hover:bg-[#3d4056]"
            />
            <ul className="space-y-2">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle2 className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
      <h2 className="text-4xl font-bold text-center mt-8 mb-8">Our Learners <span className="text-[#4a4e69]">Love</span> Lumi</h2>
      <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-8">
        {/* Testimonial 1 */}
        <div className="bg-[#EFEFEF] p-8 rounded-lg text-gray-800">
          <p>“I struggled with language apps and classes for over two years, but I never felt truly fluent. Discovering Lumi was a game-changer. The ability to simplify and translate books, read while listening, and use the AI tutor has made my learning experience incredibly effective and enjoyable. The personalized approach kept me engaged and motivated. I highly recommend Lumi to anyone serious about learning a new language.”</p>
          <p className="mt-6 font-bold">Carlos</p>
          <p className="text-sm">Spanish Learner</p>
          <p className="text-sm">⭐️⭐️⭐️⭐️⭐️</p>
          <p className="text-sm text-gray-400">Verified purchaser ✅</p>
        </div>

        {/* Testimonial 2 */}
        <div className="bg-[#EFEFEF] p-8 rounded-lg text-gray-800">
          <p>“Learning a new language was always intimidating for me. I had tried multiple apps and courses but never made significant progress. This app changed that completely. The ability to read simplified and translated books, listen along, and use the AI tutor has transformed my learning journey. After three weeks, my comprehension and confidence in Italian have soared. I&apos;m excited about learning again and highly recommend this app to anyone looking to make real progress.”</p>
          <p className="mt-6 font-bold">Irene</p>
          <p className="text-sm">Italian Learner</p>
          <p className="text-sm">⭐️⭐️⭐️⭐️⭐️</p>
          <p className="text-sm text-gray-400">Verified purchaser ✅</p>
        </div>

        {/* Testimonial 3 */}
        <div className="bg-[#EFEFEF] p-8 rounded-lg text-gray-800">
          <p>“I had been studying Japanese on various apps for years but never felt truly proficient. Since using Lumi, my learning has accelerated beyond my expectations. The combination of book translations, AI-assisted reading, and the personalized tutor has made a huge difference. I now have a clear plan and effective tools to achieve fluency. This app has not only enhanced my language skills but also improved my overall learning habits. I couldn&apos;t be happier with the results.”</p>
          <p className="mt-6 font-bold">Sophia K</p>
          <p className="text-sm">Japanese Learner</p>
          <p className="text-sm">⭐️⭐️⭐️⭐️⭐️</p>
          <p className="text-sm text-gray-400">Verified purchaser ✅</p>
        </div>

        {/* Testimonial 4 */}
        <div className="bg-[#EFEFEF] p-8 rounded-lg text-gray-800">
          <p>“This app has been a game-changer for my Chinese learning journey. The Lumi team genuinely cares about user success and their innovative approach makes learning sustainable and enjoyable. I look forward to each session because it feels less like studying and more like a fun challenge. I highly recommend Lumi to anyone wanting a truly effective and enjoyable language learning experience. Thank you, Lumi team!”</p>
          <p className="mt-6 font-bold">Alex</p>
          <p className="text-sm">Chinese Learner</p>
          <p className="text-sm">⭐️⭐️⭐️⭐️⭐️</p>
          <p className="text-sm text-gray-400">Verified purchaser ✅</p>
        </div>
       </div>
    </div>
  );

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="default" className="animated-gradient">Browse Paid Plans</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] md:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <div className="flex flex-col h-full">
          {modalContent}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PricingModal;
